// M07 指數、指數函數與對數函數 - Main Aggregation File
// 本模組涵蓋指數、指數函數與對數函數的完整學習體系
// 包含31個主題，155道練習題（每個主題5題，每題含a、b、c三部分）

import { 
  M07_TOPICS_01_TO_10, 
  M07_PROBLEMS_01_TO_10 
} from './M07_Topics_01_to_10.js';

import { 
  M07_TOPICS_11_TO_20, 
  M07_PROBLEMS_11_TO_20 
} from './M07_Topics_11_to_20.js';

import { 
  M07_TOPICS_21_TO_31, 
  M07_PROBLEMS_21_TO_31 
} from './M07_Topics_21_to_31.js';

// ============================================================================
// 模組基本信息
// ============================================================================

export const M07_MODULE_INFO = {
  id: 'M07',
  name: '指數、指數函數與對數函數',
  description: '全面掌握指數運算、對數計算、函數性質與實際應用',
  category: 'advanced',
  difficulty: 'intermediate',
  estimatedTime: '15-20小時',
  prerequisites: ['M01', 'M02', 'M03'],
  learningObjectives: [
    '熟練掌握指數定律和對數性質',
    '能夠進行指數與對數的相互轉換',
    '理解指數函數和對數函數的性質',
    '解決涉及指數對數的方程和不等式',
    '應用指數對數模型解決實際問題'
  ],
  keyTopics: [
    '指數定律與運算',
    '對數定義與性質',
    '指數對數方程',
    '函數性質與圖像',
    '實際應用模型'
  ],
  totalQuestions: 155,
  totalTopics: 31,
  questionsPerTopic: 5,
  partsPerQuestion: 3,
  difficultyDistribution: {
    basic: 45,      // 基礎題 (29%)
    intermediate: 80, // 中級題 (52%)
    advanced: 30     // 進階題 (19%)
  }
};

// ============================================================================
// 主題列表聚合
// ============================================================================

export const M07_TOPICS = [
  ...M07_TOPICS_01_TO_10,
  ...M07_TOPICS_11_TO_20,
  ...M07_TOPICS_21_TO_31
];

// ============================================================================
// 問題數據聚合
// ============================================================================

export const M07_PROBLEMS_DATA = [
  ...M07_PROBLEMS_01_TO_10,
  ...M07_PROBLEMS_11_TO_20,
  ...M07_PROBLEMS_21_TO_31
];

// ============================================================================
// 輔助函數
// ============================================================================

/**
 * 獲取所有M07問題
 * @returns {Array} 所有問題的數組
 */
export function getAllM07Problems() {
  return M07_PROBLEMS_DATA;
}

/**
 * 根據主題ID獲取問題
 * @param {string} topicId - 主題ID
 * @returns {Array} 該主題的所有問題
 */
export function getProblemsByTopicId(topicId) {
  return M07_PROBLEMS_DATA.filter(problem => problem.topicId === topicId);
}

/**
 * 根據難度級別獲取問題
 * @param {string} difficulty - 難度級別 ('basic', 'intermediate', 'advanced')
 * @returns {Array} 指定難度的所有問題
 */
export function getProblemsByDifficulty(difficulty) {
  return M07_PROBLEMS_DATA.filter(problem => problem.difficulty === difficulty);
}

/**
 * 根據主題名稱獲取主題信息
 * @param {string} topicName - 主題名稱
 * @returns {Object|null} 主題信息對象
 */
export function getTopicByName(topicName) {
  return M07_TOPICS.find(topic => topic.name === topicName) || null;
}

/**
 * 獲取指定範圍的主題
 * @param {number} startIndex - 開始索引
 * @param {number} endIndex - 結束索引
 * @returns {Array} 指定範圍的主題數組
 */
export function getTopicsInRange(startIndex, endIndex) {
  return M07_TOPICS.slice(startIndex, endIndex + 1);
}

/**
 * 根據應用領域分類問題
 * @returns {Object} 按應用領域分類的問題
 */
export function getProblemsByApplication() {
  const applications = {
    algebraic: [],      // 代數運算
    functional: [],     // 函數性質
    equations: [],      // 方程求解
    inequalities: [],   // 不等式
    applications: []    // 實際應用
  };

  M07_PROBLEMS_DATA.forEach(problem => {
    const topicId = problem.topicId;
    const topicNumber = parseInt(topicId.split('_')[1]);
    
    if (topicNumber <= 10) {
      applications.algebraic.push(problem);
    } else if (topicNumber <= 20) {
      applications.equations.push(problem);
    } else if (topicNumber <= 25) {
      applications.functional.push(problem);
    } else if (topicNumber <= 29) {
      applications.inequalities.push(problem);
    } else {
      applications.applications.push(problem);
    }
  });

  return applications;
}

/**
 * 獲取學習路徑建議
 * @param {string} studentLevel - 學生水平 ('beginner', 'intermediate', 'advanced')
 * @returns {Array} 建議的學習順序
 */
export function getLearningPath(studentLevel = 'intermediate') {
  const paths = {
    beginner: [
      'M07_01', 'M07_02', 'M07_03', 'M07_04', 'M07_16', 'M07_17', 
      'M07_18', 'M07_21', 'M07_22', 'M07_23', 'M07_25', 'M07_26'
    ],
    intermediate: [
      'M07_01', 'M07_02', 'M07_03', 'M07_04', 'M07_05', 'M07_06', 
      'M07_07', 'M07_13', 'M07_16', 'M07_17', 'M07_18', 'M07_20',
      'M07_21', 'M07_22', 'M07_28', 'M07_30', 'M07_31'
    ],
    advanced: [
      'M07_05', 'M07_06', 'M07_08', 'M07_09', 'M07_11', 'M07_12',
      'M07_14', 'M07_15', 'M07_19', 'M07_24', 'M07_27', 'M07_29'
    ]
  };

  return paths[studentLevel] || paths.intermediate;
}

/**
 * 驗證數據完整性
 * @returns {Object} 驗證結果
 */
export function validateM07Data() {
  const validation = {
    isValid: true,
    errors: [],
    warnings: [],
    statistics: {
      totalTopics: M07_TOPICS.length,
      totalProblems: M07_PROBLEMS_DATA.length,
      expectedProblems: M07_TOPICS.length * 5,
      problemsPerTopic: {}
    }
  };

  // 檢查主題數量
  if (M07_TOPICS.length !== 31) {
    validation.errors.push(`期望31個主題，實際${M07_TOPICS.length}個`);
    validation.isValid = false;
  }

  // 檢查問題總數
  if (M07_PROBLEMS_DATA.length !== 155) {
    validation.errors.push(`期望155個問題，實際${M07_PROBLEMS_DATA.length}個`);
    validation.isValid = false;
  }

  // 檢查每個主題的問題數量
  M07_TOPICS.forEach(topic => {
    const topicProblems = getProblemsByTopicId(topic.id);
    validation.statistics.problemsPerTopic[topic.id] = topicProblems.length;
    
    if (topicProblems.length !== 5) {
      validation.errors.push(`主題${topic.id}期望5個問題，實際${topicProblems.length}個`);
      validation.isValid = false;
    }

    // 檢查每個問題的部分
    topicProblems.forEach(problem => {
      if (!problem.parts || !problem.parts.a || !problem.parts.b || !problem.parts.c) {
        validation.errors.push(`問題${problem.id}缺少必要的a、b、c部分`);
        validation.isValid = false;
      }
    });
  });

  return validation;
}

/**
 * 獲取統計信息
 * @returns {Object} 模組統計信息
 */
export function getM07Statistics() {
  const stats = {
    moduleInfo: M07_MODULE_INFO,
    topicCount: M07_TOPICS.length,
    problemCount: M07_PROBLEMS_DATA.length,
    difficultyBreakdown: {
      basic: getProblemsByDifficulty('basic').length,
      intermediate: getProblemsByDifficulty('intermediate').length,
      advanced: getProblemsByDifficulty('advanced').length
    },
    topicsByDifficulty: {
      basic: M07_TOPICS.filter(t => t.difficulty === 'basic').length,
      intermediate: M07_TOPICS.filter(t => t.difficulty === 'intermediate').length,
      advanced: M07_TOPICS.filter(t => t.difficulty === 'advanced').length
    },
    applicationAreas: Object.keys(getProblemsByApplication()).map(area => ({
      area,
      count: getProblemsByApplication()[area].length
    })),
    dataIntegrity: validateM07Data()
  };

  return stats;
}

// ============================================================================
// Supabase 遷移準備
// ============================================================================

/**
 * 為 Supabase 遷移準備數據
 * @returns {Object} 格式化的數據結構
 */
export function prepareForSupabaseMigration() {
  return {
    module: {
      ...M07_MODULE_INFO,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    topics: M07_TOPICS.map(topic => ({
      ...topic,
      module_id: 'M07',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })),
    problems: M07_PROBLEMS_DATA.map(problem => ({
      ...problem,
      module_id: 'M07',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // 將 parts 轉換為 JSON 字符串以適應 Supabase
      parts_json: JSON.stringify(problem.parts)
    }))
  };
}

// ============================================================================
// 導出所有數據和函數
// ============================================================================

export default {
  MODULE_INFO: M07_MODULE_INFO,
  TOPICS: M07_TOPICS,
  PROBLEMS_DATA: M07_PROBLEMS_DATA,
  
  // 輔助函數
  getAllM07Problems,
  getProblemsByTopicId,
  getProblemsByDifficulty,
  getTopicByName,
  getTopicsInRange,
  getProblemsByApplication,
  getLearningPath,
  validateM07Data,
  getM07Statistics,
  prepareForSupabaseMigration
};

// ============================================================================
// 數據驗證（開發時運行）
// ============================================================================

// 在開發環境中自動驗證數據
if (process.env.NODE_ENV === 'development') {
  const validation = validateM07Data();
  if (!validation.isValid) {
    console.warn('M07 數據驗證失敗:', validation.errors);
  } else {
    console.log('M07 數據驗證通過 ✓');
    console.log('統計信息:', getM07Statistics());
  }
} 